// Unit models from the client's own CAD/BIM exports (glTF), built by tools/convert_models.py.
// Each GLB is already in the engine unit frame:
//   +x = wide end of the footprint (glass front / octagon), narrow end = entrance porch,
//   +z = lateral (right when facing the front = -z), y = 0 at the outside ground, origin = frame centre.
// Meshes are named by engine material key (wood, glass, roofEdge, snow, ...) and get the shared
// materials from materials.js, so bake() batches them with every other building. A part whose GLB material
// carries textures (Blender-built landmarks: kelo bark, planks) keeps its UVs and gets a textured clone of the
// library material (materials.js deriveTextured), which still follows the night light ramp.
//
// resort.json:  units.<type> = { model: "gltf", params: { url, rotationDeg, offset, mirror, materialMap, snowCap },
//                                fallback: { model, params } }   // parametric model if the GLB can't load
//   params.glow = { gain, color }: night uplight strength/colour of the textured, glow-masked parts (landmarks).
//   Glow-masked `water` parts with a colour (hot tubs) glow on the night ramp (VIS-149) and light the snow (VIS-150: glowAt).
//   params.smoke = [[x, y, z], ...]: flue tops in the GLB frame (a real stove chimney seen in the photos); with
//   buildings.<key>.smoke (or a prop's smoke) set, main.js puts chimney smoke there.
//   params.accents = [{ a: [x,y,z], b: [x,y,z], r, mat, src }]: extra rods in the unit frame for things the CAD
//   leaves out but photos show (e.g. LED strips: mat "led"). Each needs a photo `src`. A bent strip is one accent with
//   `points: [[x,y,z], ...]` instead of a/b (an A along both rakes: eave, apex, eave): one closed tube, so one light.
//   VIS-154, props only: params.interior = { url, gain, day, replaces, src, confirm }: a baked interior (tools/blender/interior_kit.py)
//   in this GLB's own frame, seen through its glass (main.js draws every copy of one interior as one unlit mesh).
//   replaces = GLB parts (mesh keys) the interior stands in for, e.g. a plain back face where the room goes on: left out
//   only when the interior loaded, so a failed load still shows the closed shell.
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from './meshopt.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { deriveTextured } from './materials.js';
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { freeOnUpload } from './gpu/release.js';

const cache = new Map(); // url -> { parts: [{ key, geometry }], top } | null (failed)
const dropped = new Set(); // VIS-267: urls whose source shapes went (dropBuildSources)
/** VIS-267: the build loop is over on a visitor's page (main.js): let go of the models' source shapes (float32 copies of
 *  every GLB part, Aurora 5.4 MB: the merged batches hold their own), the prop interiors (cloned into their merged
 *  meshes; a compound interior's mesh keeps its own) and the picture dedup's byte copies. Nothing on that page builds a
 *  model again (the editor's LZ.propAt is the editor frame's); a later preloadGltfUnits (a lazy module) loads its urls
 *  afresh, and a model asked for after the drop is reported, not silently built as its fallback. */
export function dropBuildSources() {
  for (const url of cache.keys()) dropped.add(url);
  cache.clear(); interiors.clear(); sharedTex.clear();
}
const entryOf = (url) => {
  const e = cache.get(url);
  if (e === undefined && dropped.has(url)) { const err = new Error(`gltf unit ${url} asked for after its source shapes were dropped`); console.warn(err.message); degraded('building-model', err); }
  return e;
};
// WS-28: season flag (main.js SEASON). Outside winter the GLB's 'snow' parts (the roof snow layer) are left out.
let SEASON = 'winter';
export function setSeason(name) { SEASON = name || 'winter'; }

/** Box-projected UVs in metres/2 (same convention as units.js), so board and snow textures keep real scale. */
function boxUV(g) {
  const p = g.attributes.position, n = g.attributes.normal;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u, v;
    if (ay > ax && ay > az) { u = p.getX(i); v = p.getZ(i); }
    else if (ax > az) { u = p.getZ(i); v = p.getY(i); }
    else { u = p.getX(i); v = p.getY(i); }
    uv[i * 2] = u * 0.5; uv[i * 2 + 1] = v * 0.5;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** Library shingle materials: a GLB part drawn with one (and no UVs of its own) gets slopeUV, not the box projection. */
const SHINGLES = new Set(['shingle', 'shingleDark']);
/** Shingle UVs per roof facet in metres/2, as kota.js roofUV: u = metres along the facet's level line (the eave's
 *  direction), v = metres up the slope. A CAD roof then shows the same shingle size and row direction as a parametric
 *  kota roof (the box projection stretched them ~1.4x down a 44 degree slope and turned the rows on hip facets).
 *  Returns a non-indexed copy (each facet its own UVs); a flat or upright face keeps the box projection. */
export function slopeUV(src) {
  const g = src.index ? src.toNonIndexed() : src.clone();
  const p = g.attributes.position, uv = new Float32Array(p.count * 2);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3(), q = new THREE.Vector3();
  for (let i = 0; i + 2 < p.count; i += 3) {
    a.fromBufferAttribute(p, i); b.fromBufferAttribute(p, i + 1); c.fromBufferAttribute(p, i + 2);
    n.subVectors(b, a).cross(q.subVectors(c, a)).normalize();
    if (n.y < 0) n.negate(); // either winding: the roof's top side
    const h = Math.hypot(n.x, n.z);
    for (let k = 0; k < 3; k++) {
      q.fromBufferAttribute(p, i + k);
      let u, v;
      if (h > 0.05 && n.y > 0.05) { // a pitched face: t = level line (kota's eave tangent), d = up the slope
        u = (q.x * -n.z + q.z * n.x) / h;
        v = (q.x * -n.x * n.y + q.y * h * h + q.z * -n.z * n.y) / h;
      } else if (n.y >= h) { u = q.x; v = q.z; } // flat: as boxUV
      else if (Math.abs(n.x) > Math.abs(n.z)) { u = q.z; v = q.y; }
      else { u = q.x; v = q.y; }
      uv[(i + k) * 2] = u * 0.5; uv[(i + k) * 2 + 1] = v * 0.5;
    }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return g;
}

/** Quantized (meshopt) attributes -> plain float32 in metres, with the node transform applied. */
function plainGeometry(mesh) {
  const src = mesh.geometry;
  const g = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', ...(mesh.material?.map ? ['uv'] : [])]) { // uv only for textured parts
    const a = src.attributes[name];
    if (!a) continue;
    const n = a.itemSize, out = new Float32Array(a.count * n);
    for (let i = 0; i < a.count; i++) for (let c = 0; c < n; c++) out[i * n + c] = a.getComponent(i, c);
    g.setAttribute(name, new THREE.BufferAttribute(out, n));
  }
  // Night uplight mask from Blender (vertex colour 'glow', COLOR_0): kept as a 1-channel 'glow' attribute
  const col = src.attributes.color;
  if (col) {
    const out = new Float32Array(col.count);
    for (let i = 0; i < col.count; i++) out[i] = col.getComponent(i, 0);
    g.setAttribute('glow', new THREE.BufferAttribute(out, 1));
  }
  if (src.index) g.setIndex(Array.from(src.index.array));
  mesh.updateWorldMatrix(true, false);
  g.applyMatrix4(mesh.matrixWorld);
  if (!g.attributes.normal) g.computeVertexNormals();
  return g;
}

// WS-37 r3: baked interiors (buildings.<key>.params.interior.url, tools/blender/interior_kit.py): one mesh, positions
// + vertex colours (the baked light), already in the resort frame. url -> geometry.
// VIS-154: a gltf prop's params.interior.url too: then the interior is in the prop GLB's own frame (main.js places it).
const interiors = new Map();
/** The interior geometry for a url, or null when it is not configured or failed to load. */
export function interior(url) { return (url && interiors.get(url)) || null; }
async function loadInterior(loader, url, base) {
  try {
    const gltf = await loader.loadAsync(/^(https?:|\/)/.test(url) ? url : base + url);
    gltf.scene.updateMatrixWorld(true);
    const geos = [];
    gltf.scene.traverse((m) => {
      if (!m.isMesh) return;
      const g = new THREE.BufferGeometry();
      for (const name of ['position', 'color']) { // quantized (meshopt) -> plain float32; colour as RGB
        const a = m.geometry.attributes[name];
        if (!a) return;
        const out = new Float32Array(a.count * 3);
        for (let i = 0; i < a.count; i++) for (let c = 0; c < 3; c++) out[i * 3 + c] = a.getComponent(i, c);
        g.setAttribute(name, new THREE.BufferAttribute(out, 3));
      }
      if (m.geometry.index) g.setIndex(Array.from(m.geometry.index.array));
      g.applyMatrix4(m.matrixWorld);
      geos.push(g);
    });
    if (geos.length) interiors.set(url, geos.length === 1 ? geos[0] : mergeGeometries(geos));
  } catch (err) {
    console.warn(`interior ${url} failed to load; its windows fall back to a plain glow (a prop keeps its closed shell)`, err); // VIS-154 wording
    degraded('building-interior', err);
  }
}

// ---- WS-72 step 6 (begin): one texture per identical picture. Blender landmarks each embed the same bark pictures
// (a bark albedo + normal in each of 3 landmarks on the first map = 2 x 2.7 MB of graphics memory per extra copy); a texture whose
// embedded image bytes, colour space and sampling equal one already loaded is replaced by that one (byte-compared:
// exact).
const sharedTex = new Map();
async function shareTexture(parser, tex) {
  const ti = tex ? parser.associations.get(tex)?.textures : undefined;
  const img = ti === undefined ? null : parser.json.images?.[parser.json.textures[ti]?.source];
  if (img?.bufferView === undefined) return tex;
  const bytes = new Uint8Array(await parser.getDependency('bufferView', img.bufferView));
  let h = 2166136261;
  for (let i = 0; i < bytes.length; i += 7) h = Math.imul(h ^ bytes[i], 16777619);
  const key = [img.mimeType, bytes.length, h >>> 0, tex.colorSpace, tex.flipY, tex.wrapS, tex.wrapT, tex.magFilter, tex.minFilter, tex.channel, tex.anisotropy, ...tex.repeat, ...tex.offset, tex.rotation].join();
  const hit = sharedTex.get(key);
  if (hit && hit.bytes.length === bytes.length && hit.bytes.every((b, i) => b === bytes[i])) return hit.tex;
  if (!hit) sharedTex.set(key, { bytes, tex });
  return tex;
}
// ---- WS-72 step 6 (end)
// ---- WS-72 extras (begin): shared picture files. A picture several GLBs of a folder use is stored once beside them
// (tools/landmarks/share_textures.py, called by the landmark build) and named by uri; every GLB naming the same file
// with the same sampling gets the same texture: one download, one decode, one upload.
// VIS-267: the large pictures are KTX2 (tools/landmarks/ktx2_textures.py: KHR_texture_basisu, the kelo bark shared by
// four landmarks), so this plugin takes the place of three's KHR_texture_basisu one (same name: the loader asks plugins
// in the order they were registered, and three's would answer first) and loads them through the map's KTX2 loader.
const BASISU = 'KHR_texture_basisu';
const uriTex = new Map();
class SharedUriTextures {
  constructor(parser) { this.parser = parser; this.name = BASISU; }
  loadTexture(i) {
    const p = this.parser, td = p.json.textures[i], bu = td.extensions?.[BASISU], si = bu ? bu.source : td.source, src = p.json.images?.[si];
    if (td.extensions && !bu) return null; // another extension's: the loader's own path
    if (bu && !p.options.ktx2Loader) throw new Error('GLTFLoader: a KTX2 picture and no KTX2 loader (gltfUnits.js preloadGltfUnits ktx2)');
    const load = () => (bu ? p.loadTextureImage(i, si, p.options.ktx2Loader) : p.loadTexture(i)); // the parser's own (plugins are not asked again)
    if (!src?.uri || src.uri.startsWith('data:')) return bu ? load() : null; // embedded: one texture per GLB
    const key = `${THREE.LoaderUtils.resolveURL(src.uri, p.options.path)}|${JSON.stringify(p.json.samplers?.[td.sampler] || {})}`;
    if (!uriTex.has(key)) uriTex.set(key, load());
    return uriTex.get(key);
  }
}
/** VIS-267: GLTFLoader's KTX2 loader: the map's one (vegetation.js sharedKtx), made when a GLB first holds a KTX2 picture
 *  (main.js: ktx() gives its promise); a picture that fails is reported (three's loader leaves the part untextured; a
 *  KTX2 picture has no JPEG to fall back to, as the tree kits). */
const ktx2For = (ktx) => ({
  load: (url, ok, _progress, no) => ktx().then((l) => l.load(url, ok, undefined, (e) => { degraded('building-model', e, { picture: 'ktx2' }); no(e); }), no),
});
// ---- WS-72 extras (end)

/** Collect the GLB urls used by resort.json (units and named buildings) and load them once.
 *  ktx2: () => a promise of the map's KTX2 loader (VIS-267), asked for by GLBs with KTX2 pictures only. */
export async function preloadGltfUnits(resort, base, ktx2 = null) {
  const urls = new Set();
  const inner = new Set([...Object.values(resort.buildings || {}), ...(resort.props || []).filter((p) => p?.model === 'gltf')].map((c) => c?.params?.interior?.url).filter(Boolean)); // VIS-154: + gltf props
  // props too: Blender-built landmarks (tools/blender/run_landmark.py) use the same loader and material keys
  for (const cfg of [...Object.values(resort.units || {}), ...Object.values(resort.buildings || {}), ...(resort.props || [])]) {
    if (cfg && cfg.model === 'gltf' && cfg.params?.url) urls.add(cfg.params.url);
  }
  if (!urls.size && !inner.size) return;
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  loader.register((parser) => new SharedUriTextures(parser)); // WS-72 extras
  if (ktx2) loader.setKTX2Loader(ktx2For(ktx2)); // VIS-267
  await Promise.all([...[...inner].map((url) => loadInterior(loader, url, base)), ...[...urls].map(async (url) => {
    try {
      const gltf = await loader.loadAsync(/^(https?:|\/)/.test(url) ? url : base + url);
      gltf.scene.updateMatrixWorld(true);
      const parts = [];
      let top = 0;
      const box = new THREE.Box3();
      gltf.scene.traverse((m) => {
        if (!m.isMesh) return;
        const geometry = plainGeometry(m);
        const mt = m.material;
        const maps = mt?.map || geometry.attributes.glow
          ? { map: mt?.map || null, normalMap: mt?.normalMap || null, normalScale: mt?.normalScale, glow: !!geometry.attributes.glow } : null;
        if (maps) for (const t of [maps.map, maps.normalMap]) if (t) t.anisotropy = 8;
        if (!maps?.map) boxUV(geometry); // textured landmark parts keep their own UVs
        geometry.computeBoundingBox();
        top = Math.max(top, geometry.boundingBox.max.y);
        box.union(geometry.boundingBox);
        parts.push({ key: m.name || m.material?.name, geometry, maps });
      });
      for (const p of parts) if (p.maps) { p.maps.map = await shareTexture(gltf.parser, p.maps.map); p.maps.normalMap = await shareTexture(gltf.parser, p.maps.normalMap); } // WS-72 step 6
      for (const p of parts) if (p.maps) for (const t of [p.maps.map, p.maps.normalMap]) if (t && !t.userData.freeOnUpload) { t.userData.freeOnUpload = true; freeOnUpload(t); } // VIS-267: the decoded picture (ImageBitmap / KTX2 levels) goes once on the graphics chip (once a texture: shared ones come back)
      cache.set(url, { parts, top, plan: [box.min.x, box.max.x, box.min.z, box.max.z] });
      dropped.delete(url);
    } catch (err) {
      console.warn(`gltf unit ${url} failed to load; using the fallback model`, err);
      degraded('building-model', err);
      cache.set(url, null);
      dropped.delete(url);
    }
  })]);
}

export const gltfEntry = (url) => entryOf(url); // VIS-189: a loaded GLB's parts (lazy/cars.js)

/** Builder with the same contract as units.js: { group, top, smoke }. Returns null if the GLB is missing. */
function rod(a, b, r) {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b);
  const len = A.distanceTo(B);
  const g = new THREE.CylinderGeometry(r, r, len, 6, 1, true).translate(0, len / 2, 0);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), B.clone().sub(A).normalize()));
  g.translate(A.x, A.y, A.z);
  const n = g.toNonIndexed();
  boxUV(n);
  return n;
}

/** One rod per leg of a polyline, merged: one geometry, one strip (the legs share their bend, so one light). */
const strip = (P, r) => mergeGeometries(P.slice(1).map((p, i) => rod(P[i], p, r)));

export function gltfUnit(f, M, { url, rotationDeg = 0, offset = [0, 0, 0], mirror = false, materialMap = {}, snowCap = true, accents = [], glow = {}, smoke = [], interior: room = null } = {}) {
  const entry = entryOf(url);
  if (!entry) return null;
  const replaced = (interior(room?.url) && room.replaces) || []; // VIS-154: shell parts the loaded interior stands in for (none if it failed)
  const inner = new THREE.Group();
  const lit = new THREE.Box3(); // VIS-150: the glowing water's extent (GLB frame)
  for (const part of entry.parts) {
    const { key, geometry, maps } = part;
    if (replaced.includes(key)) continue; // VIS-154
    if (key === 'snow' && (!snowCap || (SEASON !== 'winter' && SEASON !== 'all'))) continue; // WS-28: season flag (WS-41: 'all' = live season switch, built in every season)
    const mk = materialMap[key] ?? key;
    if (mk === 'drop') continue;
    // textured parts: one derived material per GLB part (library look + night ramp, the GLB's maps)
    const mat = maps ? (part.mat ||= deriveTextured(M, mk, { ...maps, gain: glow.gain ?? 1, color: glow.color ?? null })) : (M[mk] || M.wood);
    if (maps?.glow && mk === 'water' && glow.color) lit.union(geometry.boundingBox);
    const mesh = new THREE.Mesh(!maps && SHINGLES.has(mk) ? (part.slope ||= slopeUV(geometry)) : geometry, mat); // shingles: per-facet roof UVs (kota.js roofUV)
    mesh.castShadow = !mat.transparent;
    mesh.receiveShadow = true;
    if (key === 'snow') mesh.userData.seasons = ['winter']; // WS-41: main.js bakes it into the winter-only batch
    inner.add(mesh);
  }
  for (const ac of accents) inner.add(new THREE.Mesh(strip(ac.points || [ac.a, ac.b], ac.r || 0.03), M[ac.mat] || M.led));
  inner.rotation.y = THREE.MathUtils.degToRad(rotationDeg);
  inner.position.set(offset[0] || 0, offset[1] || 0, offset[2] || 0);
  if (mirror) inner.scale.z = -1; // mirrored build: swaps left and right of the front
  const group = new THREE.Group();
  group.add(inner);
  inner.updateMatrix();
  const flues = smoke.map((p) => new THREE.Vector3(...p).applyMatrix4(inner.matrix).toArray()); // GLB frame -> group frame
  // VIS-150: glowing water lights its surroundings at night: the middle of its surface, in the group frame (main.js
  // propGlows -> lazy/lightcast.js glows: a soft light in the glow colour over it)
  const glowAt = lit.isEmpty() ? null : new THREE.Vector3((lit.min.x + lit.max.x) / 2, lit.max.y, (lit.min.z + lit.max.z) / 2).applyMatrix4(inner.matrix).toArray();
  return { group, top: entry.top + 0.4, smoke: flues, plan: entry.plan, model: inner, glowAt }; // plan = [minX, maxX, minZ, maxZ] in the model frame (props: clearing, shadow); model = the GLB frame's node (VIS-154: a prop's interior follows it)
}
